import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { ArrowLeftRight, Bot, CheckCircle2, GitMerge, Pencil, RefreshCw, ScanSearch, XCircle } from "lucide-react";
import { useApi, useAuth } from "@ncp/api-client";
import { dataSourceText, formatDate, formatDateTime, formatNumber, useLocale, useTranslation, geographyText } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Checkbox, ConfidenceMeter, EmptyState, Input, Skeleton, cn, InfoHint, SourceText, useSave } from "@ncp/ui";
import { useDuplicates, useLineage, useMerges, useOverview, type DataSourceRow, type EntityRef } from "./api";
import { SourceBadge } from "./EntitiesTab";
import { useThresholdFigures } from "../case/figures";
import { FigureInfo } from "../case/MethodInfo";
import { useConfirmAction } from "../../components/confirmAction";

/** Why two records look alike, in the reader's language; the stored reason is the English one the Curator wrote. */
function useMatchReason() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  return (primary: EntityRef | null, duplicate: EntityRef | null, similarity: number, stored: string) => {
    if (locale !== "ar" || !primary || !duplicate) return stored;
    const score = Math.round(similarity * 100);
    return primary.registryNumber && !duplicate.registryNumber
      ? t("backoffice:dataBank.matchReason.unlicensed", { score })
      : t("backoffice:dataBank.matchReason.names", { score, emirate: t(`backoffice:dataBank.matchReason.sameEmirate.${primary.emirate && primary.emirate === duplicate.emirate ? "yes" : "unknown"}`) });
  };
}

function EntityChip({ e, role }: { e: EntityRef | null; role: "primary" | "duplicate" }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  if (!e) return <span className="text-sm text-muted-foreground">—</span>;
  return (
    <div className={role === "primary" ? "rounded-xl border border-success-200 bg-success-50/50 p-3" : "rounded-xl border border-dashed border-sand-300 p-3"}>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{t(`backoffice:dataBank.${role}`)}</p>
      <p className="text-sm font-semibold" dir="auto"><SourceText text={locale === "ar" ? e.nameAr : e.nameEn} /></p>
      <p className="text-xs text-muted-foreground" dir="auto"><SourceText text={locale === "ar" ? e.nameEn : e.nameAr} /></p>
      <p className="mt-1 font-mono text-[11px] text-muted-foreground">{e.registryNumber ?? t("backoffice:dataBank.unregistered")}{e.emirate ? ` · ${geographyText(e.emirate, locale)}` : ""}</p>
    </div>
  );
}

/**
 * Default confidence of a source, editable inline by the Director / Economic Expert / Admin: the provenance weight every figure ingested from that
 * source receives (the Federal Customs Authority above open data, self-reported company figures below regulators).
 */
function SourceConfidenceEditor({ source, onSaved }: { source: DataSourceRow; onSaved: () => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const { user } = useAuth();
  const canEdit = (user?.roles ?? []).some((r) => ["Admin", "Director", "EconomicExpert"].includes(r));
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(Math.round(source.defaultConfidence * 100));
  const [applyExisting, setApplyExisting] = useState(true);
  const save = useSave({
    mutationFn: () => api.put<{ touched: number }>(`/api/databank/sources/${source.id}/confidence`, { defaultConfidence: value / 100, applyToExistingRecords: applyExisting, reason: "Adjusted from the Data Bank curation workbench" }),
    saved: (r) => t("backoffice:dataBank.confidenceSaved", { count: r.touched }),
    onSuccess: () => { setEditing(false); onSaved(); },
  });
  const pct = Math.round(source.defaultConfidence * 100);
  if (!editing) {
    return (
      <button type="button" disabled={!canEdit} title={canEdit ? t("backoffice:dataBank.editConfidence") : undefined} onClick={() => { setValue(pct); setEditing(true); }} className={cn("inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium tabular", pct >= 90 ? "bg-success-100 text-success-800" : pct >= 70 ? "bg-warning-100 text-warning-900" : "bg-sand-200 text-sand-800", canEdit && "hover:ring-2 hover:ring-aegold-300")}>
        {t("backoffice:dataBank.defaultConfidence")} {pct}%{canEdit ? <Pencil className="size-3 opacity-60" /> : null}
      </button>
    );
  }
  return (
    <div className="flex items-center gap-2 rounded-xl border border-aegold-200 bg-aegold-50/40 p-2">
      <Input type="number" min={0} max={100} step={5} value={value} onChange={(e) => setValue(Math.max(0, Math.min(100, Number(e.target.value))))} className="h-8 w-20 text-center" dir="ltr" />
      <span className="text-xs">%</span>
      <label className="flex items-center gap-1 text-[11px] text-muted-foreground"><Checkbox checked={applyExisting} onCheckedChange={(v) => setApplyExisting(v === true)} /> {t("backoffice:dataBank.applyExisting")}</label>
      <Button size="sm" variant="gold" className="h-8" loading={save.isPending} onClick={() => save.mutate()}>{t("actions.save")}</Button>
      <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditing(false)}>{t("actions.cancel")}</Button>
    </div>
  );
}

/** Curator workbench: merge proposals awaiting a researcher, the live duplicate scan, source syncs and the recent lineage. */
export function CurationTab() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const confirmAction = useConfirmAction();
  const overview = useOverview();
  const merges = useMerges();
  const duplicates = useDuplicates();
  const lineage = useLineage(40);
  const invalidate = () => void qc.invalidateQueries({ queryKey: ["databank"] });
  const matchReason = useMatchReason();
  const merge = useSave({ mutationFn: (v: { primaryId: string; duplicateId: string; reason?: string }) => api.post<{ nameEn: string }>("/api/databank/merges", v), saved: (r) => t("backoffice:dataBank.merged", { name: r.nameEn }), onSuccess: invalidate });
  const reject = useSave({ mutationFn: (id: string) => api.post(`/api/databank/merges/${id}/reject`, {}), saved: t("backoffice:dataBank.mergeRejected"), onSuccess: invalidate });
  const sync = useSave({ mutationFn: (source: string) => api.post<{ records: number }>(`/api/databank/sync/${source}`, {}), saved: (r, source) => t("backoffice:dataBank.synced", { source: t(`backoffice:dataBank.sourceKeys.${source}`), count: r.records }), onSuccess: invalidate });
  const runCurator = useSave({ mutationFn: () => api.post("/api/databank/curate", {}), saved: t("backoffice:dataBank.curatorQueued") });
  const pendingIds = new Set((merges.data ?? []).flatMap((m) => [m.primary?.id, m.duplicate?.id]));
  const weak = (duplicates.data ?? []).filter((d) => !(pendingIds.has(d.primary.id) && pendingIds.has(d.duplicate.id)));

  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
      <div className="min-w-0 space-y-6">
        <Card>
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div><CardTitle className="flex items-center gap-2 text-base"><GitMerge className="size-4 text-aegold-700" /> {t("backoffice:dataBank.mergeProposals")} <Badge variant="gold" size="sm">{merges.data?.length ?? 0}</Badge><InfoHint>{t("backoffice:dataBank.mergeProposalsHint")}</InfoHint></CardTitle></div>
              <Button size="sm" variant="outline" loading={runCurator.isPending} onClick={() => runCurator.mutate()}><Bot /> {t("backoffice:dataBank.runCurator")}</Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {merges.isLoading ? <Skeleton className="h-32" /> : (merges.data ?? []).length === 0 ? <EmptyState icon={CheckCircle2} title={t("backoffice:dataBank.noMerges")} description={t("backoffice:dataBank.noMergesHint")} /> : merges.data!.map((m) => (
              <div key={m.id} className="rounded-2xl border border-sand-200 p-4">
                <div className="grid items-center gap-3 md:grid-cols-[1fr_auto_1fr]">
                  <EntityChip e={m.primary} role="primary" />
                  <div className="flex flex-col items-center gap-1 text-xs text-muted-foreground"><ArrowLeftRight className="size-5 text-aegold-700" /><span className="tabular font-semibold text-foreground">{Math.round(m.similarity * 100)}%</span></div>
                  <EntityChip e={m.duplicate} role="duplicate" />
                </div>
                <p className="mt-3 text-sm text-muted-foreground" dir="auto">{matchReason(m.primary, m.duplicate, m.similarity, m.reasoning)}</p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="gold" disabled={!m.primary || !m.duplicate} loading={merge.isPending && merge.variables?.duplicateId === m.duplicate?.id} onClick={() => void confirmAction("mergeRecords", () => merge.mutate({ primaryId: m.primary!.id, duplicateId: m.duplicate!.id, reason: m.reasoning }))}><GitMerge /> {t("backoffice:dataBank.approveMerge")}</Button>
                  <Button size="sm" variant="ghost" loading={reject.isPending && reject.variables === m.id} onClick={() => reject.mutate(m.id)}><XCircle /> {t("backoffice:dataBank.keepSeparate")}</Button>
                  {m.proposalId ? <Button size="sm" variant="link" onClick={() => navigate("/inbox")}>{t("backoffice:dataBank.openInInbox")}</Button> : null}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><ScanSearch className="size-4 text-seablue-700" /> {t("backoffice:dataBank.duplicateScan")} <Badge variant="outline" size="sm">{weak.length}</Badge><InfoHint>{t("backoffice:dataBank.duplicateScanHint")}</InfoHint></CardTitle></CardHeader>
          <CardContent>
            {duplicates.isLoading ? <Skeleton className="h-20" /> : weak.length === 0 ? <p className="text-sm text-muted-foreground">{t("backoffice:dataBank.noDuplicates")}</p> : (
              <table className="w-full text-sm">
                <thead className="text-xs text-sand-600"><tr className="border-b border-sand-200 [&>th]:px-3 [&>th]:py-2 [&>th]:text-start"><th>{t("backoffice:dataBank.primary")}</th><th>{t("backoffice:dataBank.duplicate")}</th><th>{t("backoffice:dataBank.similarity")}</th><th /></tr></thead>
                <tbody>
                  {weak.map((d) => (
                    <tr key={`${d.primary.id}-${d.duplicate.id}`} className="border-b border-sand-100 [&>td]:px-3 [&>td]:py-2">
                      <td dir="auto"><p className="font-medium"><SourceText text={locale === "ar" && d.primary.nameAr ? d.primary.nameAr : d.primary.nameEn} /></p><p className="font-mono text-[11px] text-muted-foreground">{d.primary.registryNumber ?? "—"}</p></td>
                      <td dir="auto"><p className="font-medium"><SourceText text={locale === "ar" && d.duplicate.nameAr ? d.duplicate.nameAr : d.duplicate.nameEn} /></p><p className="text-[11px] text-muted-foreground">{matchReason(d.primary, d.duplicate, d.similarity, d.reason)}</p></td>
                      <td className="w-28"><ConfidenceMeter value={d.similarity} labelled={false} /></td>
                      <td className="text-end"><Button size="sm" variant="outline" loading={merge.isPending && merge.variables?.duplicateId === d.duplicate.id} onClick={() => void confirmAction("mergeRecords", () => merge.mutate({ primaryId: d.primary.id, duplicateId: d.duplicate.id, reason: d.reason }))}><GitMerge /> {t("backoffice:dataBank.merge")}</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="min-w-0 space-y-6">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">{t("backoffice:dataBank.sourcesTitle")}<InfoHint>{t("backoffice:dataBank.sourcesHint")}</InfoHint></CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <p className="inline-flex items-start gap-1 text-xs text-muted-foreground">{t("backoffice:dataBank.confidenceHint")}<FigureInfo measure="dataBankConfidence" className="size-4" /></p>
            {(overview.data?.sources ?? []).map((s) => (
              <div key={s.key} className="flex items-center gap-3 rounded-xl border border-sand-200 p-3">
                <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium">{dataSourceText(s.key, s.name, locale)}</p><p className="text-xs text-muted-foreground">{t(`backoffice:dataBank.sourceKinds.${s.kind}`, { defaultValue: s.kind })}{s.lastSyncAt ? ` · ${t("backoffice:dataBank.lastSync")} ${formatDateTime(s.lastSyncAt, locale)} (${s.recordCount})` : ""}</p></div>
                <SourceConfidenceEditor source={s} onSaved={invalidate} />
                {["registry", "fcsc", "mof"].includes(s.key) ? <Button size="sm" variant="ghost" title={t("backoffice:dataBank.syncNow")} loading={sync.isPending && sync.variables === s.key} onClick={() => sync.mutate(s.key)}><RefreshCw /></Button> : null}
              </div>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">{t("backoffice:dataBank.lineage")}<InfoHint>{t("backoffice:dataBank.lineageHint")}</InfoHint></CardTitle></CardHeader>
          <CardContent>
            {lineage.isLoading ? <Skeleton className="h-40" /> : (
              <ol className="max-h-[32rem] space-y-1.5 overflow-y-auto text-xs" tabIndex={0}>
                {(lineage.data ?? []).map((l) => (
                  <li key={l.id} className="rounded-lg bg-sand-50 px-3 py-2">
                    <div className="flex flex-wrap items-center gap-2"><SourceBadge source={l.sourceKey} /><span className="font-medium">{t(`backoffice:dataBank.operations.${l.operation}`, { defaultValue: l.operation })}</span><Badge variant="outline" size="sm">{t(`backoffice:dataBank.tables.${l.targetTable}`, { defaultValue: l.targetTable })}</Badge><span className="ms-auto text-muted-foreground">{formatDateTime(l.createdAt, locale)}</span></div>
                    {l.sourceReference ? <p className="mt-0.5 truncate text-muted-foreground" title={l.sourceReference} dir="auto"><SourceText text={l.sourceReference} /></p> : null}
                    {l.afterJson ? <LineageFields json={l.afterJson} /> : null}
                  </li>
                ))}
              </ol>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/** Monitored deals record Deal Watch's own statuses, which have Deal Watch's labels. */
const DEAL_ENUMS: Record<string, string> = { status: "status", filingStatus: "filing" };

function LineageFields({ json }: { json: string }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const marks = useThresholdFigures();
  let fields: [string, unknown][];
  try {
    const parsed: unknown = JSON.parse(json);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    fields = Object.entries(parsed as Record<string, unknown>).filter(([, v]) => v !== null && v !== "" && !(Array.isArray(v) && v.length === 0));
  } catch { return null; }
  if (fields.length === 0) return null;
  const show = (key: string, v: unknown) => {
    if (typeof v === "number") return /Aed$/i.test(key) ? marks.aed(v) : /Percent$/i.test(key) ? marks.share(v) : formatNumber(v, locale);
    if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) return formatDate(v, locale);
    if (Array.isArray(v)) return <SourceText text={v.join("، ")} />;
    if (typeof v === "string" && DEAL_ENUMS[key]) return t(`backoffice:dealWatch.${DEAL_ENUMS[key]}.${v}`, { defaultValue: "" }) || <SourceText text={v} />;
    if (typeof v === "string") return <SourceText text={key === "Emirate" ? geographyText(v, locale) : v} />;
    return String(v);
  };
  return (
    <dl className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-muted-foreground">
      {fields.slice(0, 6).map(([k, v]) => (
        <div key={k} className="flex min-w-0 gap-1"><dt>{t(`backoffice:dataBank.lineageFields.${k}`, { defaultValue: k })}:</dt><dd className="min-w-0 break-words text-foreground">{show(k, v)}</dd></div>
      ))}
    </dl>
  );
}
